import clsx from 'clsx';
import { BasicProductData } from 'components/layout/modals/compatible-combinations-browser-modal';
import { ReactNode } from 'react';
import { basicProductTitle } from 'utils/basic-product-title';
import { formatPrice } from 'utils/helpers';
import { productImageUrl } from 'utils/system-products';

type DuctedProductRowProps = Readonly<{
  caption: string;
  modelText: string;
  product?: BasicProductData | null;
  leading?: ReactNode;
  actions?: ReactNode;
  as?: 'div' | 'label';
}>;

function ProductPhoto({ product }: { product: BasicProductData | undefined }) {
  if (product?.photo) {
    return (
      <img
        src={productImageUrl(product.photo)}
        alt={product.name}
        className='h-16 w-16 shrink-0 rounded border border-gray-200 object-contain'
      />
    );
  }
  return (
    <div
      data-testid='ducted-component-photo-fallback'
      className='h-16 w-16 shrink-0 rounded border border-gray-200 bg-gray-50'
    />
  );
}

export default function DuctedProductRow({
  caption,
  modelText,
  product,
  leading,
  actions,
  as: Wrapper = 'div'
}: DuctedProductRowProps) {
  const resolved = product ?? undefined;
  const inStock = resolved != null && resolved.fulfillableStock > 0;
  const title = basicProductTitle(resolved, modelText);
  return (
    <Wrapper
      data-testid='ducted-product-row'
      className={clsx(
        'flex items-center gap-3 bg-white p-3',
        Wrapper === 'label' && 'cursor-pointer'
      )}
    >
      {leading}
      <ProductPhoto product={resolved} />
      <div className='flex min-w-0 grow flex-col gap-0.5'>
        <div className='text-[10px] font-semibold uppercase tracking-wider text-gray-500'>
          {caption}
        </div>
        <div className='line-clamp-2 text-sm font-semibold text-gray-900'>
          {title}
        </div>
        {title !== modelText && (
          <div className='text-xs text-gray-500'>{modelText}</div>
        )}
        {resolved && (
          <div
            className={clsx(
              'text-xs font-medium',
              inStock ? 'text-green-600' : 'text-red-600'
            )}
          >
            {inStock ? 'In stock' : 'Out of stock'}
          </div>
        )}
      </div>
      <div className='flex shrink-0 flex-col items-end gap-0.5'>
        <div
          className={clsx(
            'text-sm font-semibold',
            resolved?.isOnSale ? 'text-red-600' : 'text-gray-900'
          )}
        >
          {resolved && resolved.price != null ? (
            <>
              <span>{formatPrice(resolved.price)}</span>
              {resolved.isOnSale && (
                <span className='ml-1 text-[10px] font-medium uppercase tracking-wider text-red-600'>
                  Sale
                </span>
              )}
            </>
          ) : (
            <span className='text-gray-400'>—</span>
          )}
        </div>
        {resolved?.productUrl && (
          <a
            href={`https://alpinehomeair.com${resolved.productUrl}`}
            target='_blank'
            rel='noreferrer'
            className='text-xs font-semibold text-blue-600 hover:text-blue-800'
          >
            View product →
          </a>
        )}
      </div>
      {actions && (
        <div className='flex shrink-0 items-center gap-1.5'>{actions}</div>
      )}
    </Wrapper>
  );
}
